import {memo, useMemo} from 'react';
import {Link} from '@ezcater/tapas';
import useTranslation from 'next-translate/useTranslation';
import {twJoin} from 'tailwind-merge';

import EzOExperience from '@/components/global/experiences/EzOExperience';
import MarketplaceExperience from '@/components/global/experiences/MarketplaceExperience';
import EppoFlags from '@/EppoFlags';
import {useStringAssignmentExperimentDetails} from '@/eppoHooks';
import useEzCaterCompanyInfo from '@/hooks/useEzCaterCompanyInfo';
import useOrderingExperience from '@/hooks/useOrderingExperience';
import useRAFImprovements from '@/hooks/useRAFImprovements';
import {
  ABOUT_US_PATH,
  ACCESSIBILITY_PATH,
  BLOG_PATH,
  CA_NOTICE_OF_COLLECTION_PATH,
  CAREERS_PATH,
  CATERER_PARTNERS_PATH,
  COMPANY_NEWS_PATH,
  CONTACT_PATH,
  CORPORATE_SOLUTIONS_PATH,
  CUSTOMER_HELP_CENTER_URL,
  PHYSICAL_MAIL_OPT_OUT_URL,
  PRIVACY_PATH,
  REWARDS_PATH,
  TERMS_PATH,
} from '@/paths';
import AppDownloadCta from './AppDownloadCta';
import AppDownloadCtaMobile from './AppDownloadCtaMobile';
import Disclosure from './Disclosure';
import ReferAFriendCta from './ReferAFriendCta';
import SocialLinks from './SocialLinks';
import TrackedLinks, {type TrackedLink} from './TrackedLinks';
import TranscendButton from './TranscendButton';

const optOutLinks = (isHelpCenterTreatment: boolean): TrackedLink[] =>
  isHelpCenterTreatment ? [{key: 'optOutOfPhysicalMailing', path: PHYSICAL_MAIL_OPT_OUT_URL}] : [];

const helpLinks: TrackedLink[] = [
  {key: 'contactUs', path: CONTACT_PATH},
  {key: 'customerHelpCenter', path: CUSTOMER_HELP_CENTER_URL},
];

const navLinks: TrackedLink[] = [
  {key: 'aboutUs', path: ABOUT_US_PATH},
  {key: 'careers', path: CAREERS_PATH},
  {key: 'blog', path: BLOG_PATH},
  ...helpLinks,
  {key: 'becomeACaterer', path: CATERER_PARTNERS_PATH},
  {key: 'corporateSolutions', path: CORPORATE_SOLUTIONS_PATH},
];

const companyLinks: TrackedLink[] = [
  {key: 'aboutUs', path: ABOUT_US_PATH},
  {key: 'newsAndEvents', path: COMPANY_NEWS_PATH},
  {key: 'blog', path: BLOG_PATH},
  ...helpLinks,
  {key: 'careers', path: CAREERS_PATH},
  {key: 'terms', path: TERMS_PATH},
  {key: 'accessibility', path: ACCESSIBILITY_PATH},
  {key: 'privacy', path: PRIVACY_PATH},
];

const buildMutedLinks = (isHelpCenterTreatment: boolean): TrackedLink[] => [
  {key: 'privacy', path: PRIVACY_PATH},
  {key: 'terms', path: TERMS_PATH},
  {key: 'caNoticeAtCollection', path: CA_NOTICE_OF_COLLECTION_PATH},
  {key: 'accessibility', path: ACCESSIBILITY_PATH},
  ...optOutLinks(isHelpCenterTreatment),
];

const ezoMutedLinks = [
  {key: 'privacy', path: PRIVACY_PATH},
  {key: 'terms', path: TERMS_PATH},
  {key: 'accessibility', path: ACCESSIBILITY_PATH},
];

const buildMarketplaceMobileLinks = (isHelpCenterTreatment: boolean): TrackedLink[] => [
  {key: 'caNoticeAtCollection', path: CA_NOTICE_OF_COLLECTION_PATH},
  ...optOutLinks(isHelpCenterTreatment),
  {key: 'doNotSellMyInfo', path: PRIVACY_PATH},
];

const discoverLinks = [
  {key: 'becomeACaterer', path: CATERER_PARTNERS_PATH},
  {key: 'corporateSolutions', path: CORPORATE_SOLUTIONS_PATH},
  {key: 'rewards', path: REWARDS_PATH},
];

const Copyright = () => <>&copy; {new Date().getFullYear()} ezCater, Inc.</>;

type MarketplaceFooterProps = {
  orderNumber?: number | null;
  customBrandText?: string;
  showAppAndReferralCtas?: boolean;
};

const MarketplaceFooter = ({
  orderNumber,
  customBrandText,
  showAppAndReferralCtas = true,
}: MarketplaceFooterProps) => {
  const {t} = useTranslation('common');
  const companyInfo = useEzCaterCompanyInfo();
  const supportPhoneNumber = companyInfo?.supportPhoneNumber;
  const {isEater} = useOrderingExperience();
  const {showRAFImprovements} = useRAFImprovements();

  const {variation} = useStringAssignmentExperimentDetails(EppoFlags.IntercomHelpCenter);
  const isHelpCenterTreatment = variation === 'treatment';

  const mutedLinks = useMemo(() => buildMutedLinks(isHelpCenterTreatment), [isHelpCenterTreatment]);
  const marketplaceMobileLinks = useMemo(
    () => buildMarketplaceMobileLinks(isHelpCenterTreatment),
    [isHelpCenterTreatment],
  );

  return (
    <>
      <footer
        aria-label="App Footer"
        className="hidden w-full border-t border-gray-300 p-8 text-center text-sm desktop:block"
      >
        <MarketplaceExperience>
          <ul
            className={twJoin(
              'flex list-none flex-wrap justify-center gap-x-4 gap-y-2',
              !showAppAndReferralCtas && 'mb-4',
            )}
          >
            <TrackedLinks links={navLinks} />
          </ul>
          {showAppAndReferralCtas && (
            <div className="mx-auto my-7 flex max-w-pageContent gap-3">
              <AppDownloadCta />
              <ReferAFriendCta />
            </div>
          )}
        </MarketplaceExperience>

        <EzOExperience>{customBrandText && <p>{customBrandText}</p>}</EzOExperience>

        <ul className="flex list-none flex-wrap justify-center gap-x-4 gap-y-2">
          <EzOExperience>
            <li>{t('components.Footer.ezOrderingPoweredBy')}</li>
          </EzOExperience>

          <li>
            <Copyright />
          </li>

          <EzOExperience>
            <TrackedLinks links={ezoMutedLinks} />
            <TranscendButton as="li" />
          </EzOExperience>

          <MarketplaceExperience>
            <TrackedLinks links={mutedLinks} />
            <TranscendButton as="li" />
          </MarketplaceExperience>
        </ul>

        <MarketplaceExperience>
          <SocialLinks />
        </MarketplaceExperience>
      </footer>

      {!isEater && (
        <footer
          aria-label="App Footer"
          className={twJoin(
            'block w-full border-t border-gray-300 p-8 text-center text-sm font-normal desktop:hidden',
            !showRAFImprovements && 'bg-gray-100',
          )}
        >
          <MarketplaceExperience>
            <div className="flex flex-col gap-3">
              {showAppAndReferralCtas && (
                <>
                  <AppDownloadCtaMobile />
                  <ReferAFriendCta />
                </>
              )}
              <div
                className={twJoin(
                  showRAFImprovements
                    ? 'rounded-2xl bg-peppercorn-50 py-5'
                    : 'rounded-md bg-white py-5',
                )}
              >
                <div className="my-0 font-black uppercase tracking-wide">
                  {t('components.Footer.supportBox.question')}
                </div>

                {supportPhoneNumber && (
                  <Link
                    href={`tel:${supportPhoneNumber}`}
                    title={t('components.Footer.supportBox.supportPhoneTitle')}
                  >
                    {supportPhoneNumber}
                  </Link>
                )}

                {orderNumber && (
                  <div>{t('components.Footer.supportBox.referenceNumber', {orderNumber})}</div>
                )}
              </div>
            </div>

            <Disclosure label="Company">
              <ul className="m-0 flex list-none flex-col gap-2 pt-4 text-left text-base">
                <TrackedLinks links={companyLinks} />
                <MarketplaceExperience>
                  <TrackedLinks links={marketplaceMobileLinks} />
                </MarketplaceExperience>
              </ul>
            </Disclosure>

            <Disclosure label="Discover">
              <ul className="m-0 flex list-none flex-col gap-2 pt-4 text-left text-base">
                <TrackedLinks links={discoverLinks} />
              </ul>
            </Disclosure>
          </MarketplaceExperience>

          <EzOExperience>
            {customBrandText && <p>{customBrandText}</p>}
            <ul className="m-0 flex list-none flex-col gap-2 pt-4 text-left text-base">
              <li>{t('components.Footer.ezOrderingPoweredBy')}</li>
              <li>
                <Copyright />
              </li>
              <TrackedLinks links={ezoMutedLinks} />
            </ul>
          </EzOExperience>

          <MarketplaceExperience>
            <SocialLinks />
            <div className="mt-3">
              <Copyright />
            </div>
          </MarketplaceExperience>
        </footer>
      )}
    </>
  );
};

export default memo(MarketplaceFooter);
